import { useEffect, useMemo, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useFormContext } from "react-hook-form";

import FrequencySelect from "~/components/common/FrequencySelect";
import { useTeam } from "~/contexts/TeamProvider";
import {
  CRAWLER_FILE_TYPES,
  CRAWL_MODE,
  UNLIMITED_PAGE_SCALE,
  UNLIMITED_PLAN_PAGE_BUDGET,
} from "~/helpers/consts";

import FormLabel from "../login/formLabel/FormLabel";
import Switch from "../switch/Switch";
import RangeSlider from "../ui/rangeSlider/RangeSlider";
import Spinner from "../ui/spinner/Spinner";
import { SiteWithAuth } from "./General";
import styles from "./SiteSettings.module.scss";

const CRAWL_CONCURRENCY_VALUES = [25, 50, 70, 100] as const;
type CrawlConcurrency = (typeof CRAWL_CONCURRENCY_VALUES)[number];

type CrawlerSettingsProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
  /** null when the plan has no page cap (usage-billed plans) */
  pageMax: number | null;
};

const CrawlerSettings = ({
  siteData,
  isLoading,
  pageMax,
}: CrawlerSettingsProps) => {
  const { team } = useTeam();
  const site = siteData?.site;
  const canUpdateSite = siteData?.authorizations?.member
    ?.flat()
    .includes("update");
  //@ts-expect-error BE types
  const siteFile = site?.page_import?.input_file;
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const { watch, setValue, formState } = useFormContext();

  const crawlConcurrencyValue = watch("crawl_concurrency") as CrawlConcurrency;

  const isUnlimitedPages = pageMax === null;

  // A null override means "the plan's cap" — on unlimited plans the
  // backend substitutes its per-site budget instead.
  const crawlPageValue = useMemo(() => {
    const value = watch("page_max_override");

    if (value !== null && value !== undefined) return value;

    return isUnlimitedPages ? UNLIMITED_PLAN_PAGE_BUDGET : pageMax;
  }, [watch("page_max_override"), pageMax]);
  const crawlMode = watch("crawl_mode");
  watch("page_imports_attributes.input_file");
  const crawlFrequencyOverride = watch("crawl_frequency_override");

  const currentLimitPageMax =
    !isUnlimitedPages && crawlPageValue === pageMax
      ? "unlimited"
      : crawlPageValue.toLocaleString();

  const handleFileChange = (incomingFile: File) => {
    setFile(incomingFile);

    setValue(
      "page_imports_attributes",
      { input_file: incomingFile },
      {
        shouldDirty: true,
      },
    );

    setFileError(null);
  };

  const handleSliderChange = (field: string) => (newValue: number) => {
    setValue(field, newValue, { shouldDirty: true });
  };

  useEffect(() => {
    if (!formState.isDirty && file) {
      setFile(null);
    }
  }, [formState.isDirty, formState.isSubmitSuccessful, file]);

  const crawlInterval = team?.subscription?.price?.product?.crawl_interval;
  const crawlIntervalEnabled = Boolean(
    crawlInterval && crawlInterval !== "Disabled",
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className={styles.settingsSection}>
      <p className={styles.settingsSectionText}>
        This section allows you to configure crawler settings for your project.
        Here, you can set the crawling limits, update frequency, and other
        configurations.
      </p>

      <div className={styles.settingsSectionCrawlerForm}>
        <div className={styles.settingsSectionGroup}>
          <div className={styles.settingsSwitchGroup}>
            <div>
              <FormLabel label="Crawl pages from the list" />
              <p className={styles.settingsSectionText}>
                URL from the list will be crawled, all other pages will be
                marked as 'excluded’
              </p>
            </div>
            <Switch
              checked={crawlMode === CRAWL_MODE.WHITELISTED}
              onChange={(event) => {
                const newValue = event.currentTarget.checked
                  ? CRAWL_MODE.WHITELISTED
                  : CRAWL_MODE.NORMAL;
                if (newValue === CRAWL_MODE.NORMAL && file) {
                  setFile(null);
                  setValue(
                    "page_imports_attributes",
                    {},
                    { shouldDirty: true },
                  );
                }
                if (
                  newValue === CRAWL_MODE.WHITELISTED &&
                  !file &&
                  !siteFile?.filename
                ) {
                  setFileError(
                    "A CSV file with URLs is required when using whitelist mode",
                  );
                } else {
                  setFileError(null);
                }

                setValue("crawl_mode", newValue, { shouldDirty: true });
              }}
              disabled={!canUpdateSite}
            />
          </div>
          {crawlMode === CRAWL_MODE.WHITELISTED && (
            <div className={styles.fileUploader}>
              {siteFile?.filename && (
                // @ts-expect-error TS2339 non-existing className
                <div className={styles.existingFileIndicator}>
                  Currently uploaded file:{" "}
                  {siteFile.url ? (
                    <a
                      href={siteFile.url}
                      download={siteFile.filename}
                      // @ts-expect-error TS2339 non-existing className
                      className={styles.fileDownloadLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {siteFile.filename}
                    </a>
                  ) : (
                    siteFile.filename
                  )}
                </div>
              )}

              <FileUploader
                disabled={crawlMode !== CRAWL_MODE.WHITELISTED}
                name="file"
                types={CRAWLER_FILE_TYPES}
                handleChange={handleFileChange}
                uploadedLabel={
                  file?.name ||
                  (siteFile?.filename
                    ? `${siteFile.filename} (from server)`
                    : "Upload a CSV file")
                }
                fileOrFiles={file}
              />

              {/*
               // @ts-expect-error TS2339 non-existing className */}
              {fileError && <p className={styles.errorMessage}>{fileError}</p>}
            </div>
          )}
        </div>

        <div className={styles.settingsSectionLeftGroup}>
          <div>
            <FrequencySelect
              label="Crawl Frequency"
              description={
                <p className={styles.settingsFrequencyNote}>
                  Note that the frequency of crawler usage affects your invoice
                </p>
              }
              defaultSeconds={team?.crawl_frequency}
              value={crawlFrequencyOverride}
              disabled={!canUpdateSite || !crawlIntervalEnabled}
              onChange={(v) =>
                setValue("crawl_frequency_override", v, { shouldDirty: true })
              }
            />
          </div>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.settingsSliderGroup}>
            <div>
              <FormLabel label="Pages to Crawl" />
              <p className={styles.settingsSectionText}>
                {isUnlimitedPages
                  ? `Set the maximum number of pages to crawl for this site. Your plan has no page cap — pages are billed as they are crawled. Current limit: ${currentLimitPageMax}`
                  : `Set the maximum number to distribute your plan's limit across
                your sites. Current limit: ${currentLimitPageMax}, ${pageMax.toLocaleString()} included in your plan`}
              </p>
            </div>
            <div className={styles.settingsSliderContainer}>
              <RangeSlider
                editableInput={true}
                valueFormat="comma"
                value={crawlPageValue}
                onChange={handleSliderChange("page_max_override")}
                min={0}
                max={pageMax ?? undefined}
                scaleStops={isUnlimitedPages ? UNLIMITED_PAGE_SCALE : undefined}
                disabled={!canUpdateSite}
                defaultValue={crawlPageValue}
              />
            </div>
          </div>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.settingsSliderGroup}>
            <div>
              <FormLabel label="Crawl Concurrency" />
              <p className={styles.settingsSectionText}>
                Reduce if you experience any performance issues.
              </p>
            </div>
            <div className={styles.settingsSliderContainer}>
              <RangeSlider
                value={crawlConcurrencyValue}
                defaultValue={crawlConcurrencyValue}
                onChange={handleSliderChange("crawl_concurrency")}
                min={CRAWL_CONCURRENCY_VALUES[0]}
                max={
                  CRAWL_CONCURRENCY_VALUES[CRAWL_CONCURRENCY_VALUES.length - 1]
                }
                step={CRAWL_CONCURRENCY_VALUES[0]}
                disabled={!canUpdateSite}
                valueFormat="percent"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrawlerSettings;
